/*
 * UserForm
 * User signs up directly to the platform
 */

import * as React from 'react';
import {
    Validators,
    FormGenerator,
    AbstractControl,
    FieldConfig,
    FormGroup,
    GroupProps
} from 'react-reactive-form';
import { Col, Button } from 'react-bootstrap';
import { components } from 'react-select';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
    constants,
    postalAndStateRequiredCountries
} from '../../constants/constants';
import { toastr } from 'react-redux-toastr';
import { FormUtil } from '../common/FormUtil';
import { withTranslation, WithTranslation } from 'react-i18next';
import { userBaseConfigControls } from '../common/UserBaseConfigControls';
import msalInstance from './MsalInstance';
import { msalFetch } from './Auth-Utils';
import { getStatesForCountry } from '../../constants/statesProvinces';
import API from '../../constants/apiEndpoints';
import AddCompanyModal from './AddCompanyModal';
import { Ibrand, Ioption } from '../../models';

const StateProvinceInput = (props: any) => {
    if (props.meta.options && props.meta.options.length > 0) {
        return FormUtil.Select(props);
    }
    return FormUtil.TextInput(props);
};

const getIsRequiredFromSelectedCountry = (selectedCountryID?: string) => {
    if (
        selectedCountryID === '' ||
        selectedCountryID === undefined ||
        selectedCountryID === null
    ) {
        return {
            state: false,
            postalCode: false
        };
    }

    return postalAndStateRequiredCountries[selectedCountryID];
};

// Field config to configure form
const fieldConfigControls = {
    tempAddress: {
        options: {
            validators: [FormUtil.validators.requiredWithTrim]
        },
        render: FormUtil.TextInput,
        meta: {
            label: 'address',
            colWidth: 12,
            colWidthSmall: 6,
            colWidthMedium: 6,
            type: 'text',
            name: 'temp-address',
            placeholder: 'Main Address',
            className: 'address'
        }
    },
    tempAddress2: {
        render: FormUtil.TextInput,
        meta: {
            label: 'address2',
            colWidth: 12,
            colWidthSmall: 6,
            colWidthMedium: 6,
            type: 'text',
            name: 'temp-address2',
            placeholder: 'Suite #, P.O. Box, Building #',
            className: 'address2'
        }
    },
    tempCity: {
        options: {
            validators: [FormUtil.validators.requiredWithTrim]
        },
        render: FormUtil.TextInput,
        meta: {
            label: 'city',
            colWidth: 12,
            colWidthSmall: 6,
            colWidthMedium: 6,
            type: 'text',
            name: 'temp-city',
            placeholder: 'Enter City',
            className: 'city'
        }
    },
    tempState: {
        options: {
            validators: [FormUtil.validators.requiredWithTrim]
        },
        render: StateProvinceInput,
        meta: {
            label: 'state',
            colWidth: 12,
            colWidthSmall: 6,
            colWidthMedium: 6,
            type: 'text',
            name: 'temp-state',
            placeholder: 'State / Province',
            className: 'state',
            options: getStatesForCountry(constants.usCountryID)
        }
    },
    tempZip: {
        options: {
            validators: [
                FormUtil.validators.requiredWithTrim,
                Validators.pattern(
                    /^[a-zA-Z0-9][a-zA-Z0-9\- ]{0,10}[a-zA-Z0-9]$/
                )
            ]
        },
        render: FormUtil.TextInput,
        meta: {
            label: 'zip',
            colWidth: 12,
            colWidthSmall: 6,
            colWidthMedium: 6,
            type: 'tel',
            name: 'temp-zip',
            placeholder: 'Postal / Zip Code',
            className: 'zip'
        }
    },
    countryID: {
        options: {
            validators: FormUtil.validators.requiredWithTrim
        },
        render: FormUtil.Select,
        meta: {
            options: constants.countries,
            label: 'user:country',
            colWidth: 12,
            colWidthSmall: 6,
            colWidthMedium: 6,
            placeholder: 'manageUserQueue:countrySearchPlaceholder',
            name: 'country',
            className: 'country'
        }
    }
};

type FieldControls = { [key: string]: GroupProps };

// display + tab order, flowing left-right across the two columns;
// phone + extension share the final column slot
const controlOrder = [
    '$field_0',
    'first',
    'countryID',
    'last',
    'phoneCountryCode',
    'brandID',
    'tempFacility',
    'position',
    'email',
    'tempAddress',
    'tempAddress2',
    'tempCity',
    'tempState',
    'tempZip',
    'phone',
    'phoneExtension'
];

interface Iprops {
    handleSubmit: (formValue: any) => void;
    handleCancel: () => void;
    loading: boolean;
}

interface IState {
    email: string;
    showAddCompanyModal: boolean;
}

class UserForm extends React.Component<Iprops & WithTranslation, IState> {
    public userForm: FormGroup | any;
    public fieldConfig: FieldConfig;
    private subscription: any;
    private brandOptions: Ioption[] = [];

    constructor(props: Iprops & WithTranslation) {
        super(props);
        this.state = {
            email: '',
            showAddCompanyModal: false
        };
        this.fieldConfig = FormUtil.translateForm(
            this.buildFieldConfig(),
            this.props.t
        );
    }

    componentDidMount() {
        const msalAccount = msalInstance.msalApp?.getAllAccounts();

        if (msalAccount?.length > 0) {
            const account = msalAccount[0];
            const claims = account.idTokenClaims;
            if (claims) {
                setTimeout(() => {
                    const email = claims.email as string;
                    const firstName = claims.given_name as string;
                    const lastName = claims.family_name as string;

                    this.setState({ email });

                    this.userForm.patchValue({
                        email,
                        first: firstName,
                        last: lastName
                    });
                }, 0);
            }
        }

        this.getBrands();

        this.subscription = this.userForm
            .get('countryID')
            .valueChanges.subscribe((value: any) => {
                if (value && value.value) {
                    this.onCountryChanges(value.value);
                }
            });

        this.userForm.patchValue({
            countryID: {
                value: constants.usCountryID,
                label: 'United States of America'
            }
        });
    }

    componentWillUnmount() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    buildFieldConfig = (): FieldConfig => {
        const baseControls = userBaseConfigControls(
            undefined,
            undefined,
            false
        ) as FieldControls;

        const controls: FieldControls = {
            ...baseControls,
            phone: {
                ...baseControls.phone,
                meta: {
                    ...baseControls.phone.meta,
                    colWidth: 8,
                    colWidthSmall: 4,
                    colWidthMedium: 4
                }
            },
            phoneExtension: {
                ...baseControls.phoneExtension,
                meta: {
                    ...baseControls.phoneExtension.meta,
                    colWidth: 4,
                    colWidthSmall: 2,
                    colWidthMedium: 2
                }
            },
            $field_0: {
                isStatic: false,
                render: () => (
                    <Col xs={12}>
                        <p className="facility-company-hint">
                            {this.props.t('facilityOrCompanyHint')}
                        </p>
                    </Col>
                )
            } as any,
            // free text only: no lookup, so facility data is never exposed before approval
            tempFacility: {
                render: FormUtil.TextInput,
                meta: {
                    label: 'facility',
                    colWidth: 12,
                    colWidthSmall: 6,
                    colWidthMedium: 6,
                    type: 'text',
                    placeholder: 'Facility name',
                    name: 'facility',
                    className: 'facility'
                }
            },
            brandID: {
                render: FormUtil.Select,
                meta: {
                    label: 'company',
                    options: this.brandOptions,
                    colWidth: 12,
                    colWidthSmall: 6,
                    colWidthMedium: 6,
                    placeholder: 'companyPlaceholder',
                    name: 'company',
                    className: 'company',
                    isClearable: true,
                    menuList: this.CompanyMenuList
                }
            },
            ...fieldConfigControls
        };

        const sortedControls = Object.keys(controls)
            .sort((a, b) => controlOrder.indexOf(a) - controlOrder.indexOf(b))
            .reduce<FieldControls>((acc, key) => {
                acc[key] = controls[key];
                return acc;
            }, {});

        return { controls: sortedControls };
    };

    getBrands = () => {
        const axiosOptions: AxiosRequestConfig = { method: 'get' };

        msalFetch(API.GET.brand.getAll, axiosOptions)
            .then((response: AxiosResponse<any>) => {
                if (!response.data) {
                    throw new Error('missing data');
                }
                this.setBrandOptions(FormUtil.convertToOptions(response.data));
            })
            .catch((error: any) => {
                console.error('failed to load companies', error);
            });
    };

    setBrandOptions = (options: Ioption[]) => {
        this.brandOptions = options;
        const brandControl = this.userForm?.get('brandID');
        if (brandControl) {
            brandControl.meta.options = options;
            brandControl.stateChanges.next();
        }
    };

    CompanyMenuList = (menuProps: any) => (
        <components.MenuList {...menuProps}>
            {menuProps.children}
            <div
                className="add-company-option"
                onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onClick={this.openAddCompanyModal}
            >
                {`+ ${this.props.t('addCompany')}`}
            </div>
        </components.MenuList>
    );

    openAddCompanyModal = () => {
        this.setState({ showAddCompanyModal: true });
    };

    handleCompanyCreated = (company: Ibrand) => {
        const option: Ioption = { value: company.id, label: company.name };
        this.setBrandOptions([
            ...this.brandOptions.filter(o => o.value !== option.value),
            option
        ]);
        this.userForm.patchValue({ brandID: option });
        this.setState({ showAddCompanyModal: false });
    };

    onCountryChanges = (value: string) => {
        const stateFormControl = this.userForm.get('tempState');
        const tempZipFormControl = this.userForm.get('tempZip');

        const isStateRequired = getIsRequiredFromSelectedCountry(value)
            ? getIsRequiredFromSelectedCountry(value).state
            : false;
        const isPostalCodeRequired = getIsRequiredFromSelectedCountry(value)
            ? getIsRequiredFromSelectedCountry(value).postalCode
            : false;

        stateFormControl.clearValidators();
        tempZipFormControl.clearValidators();

        stateFormControl.meta.options = getStatesForCountry(value);

        if (isStateRequired) {
            stateFormControl.setValidators([
                FormUtil.validators.requiredWithTrim
            ]);
            stateFormControl.patchValue(null);
        } else {
            stateFormControl.patchValue(null);
        }

        this.forceUpdate();

        if (isPostalCodeRequired) {
            tempZipFormControl.setValidators([
                FormUtil.validators.requiredWithTrim,
                Validators.pattern(
                    /^[a-zA-Z0-9][a-zA-Z0-9\- ]{0,10}[a-zA-Z0-9]$/
                )
            ]);
            tempZipFormControl.patchValue(null);
        } else {
            tempZipFormControl.patchValue(null);
        }
    };

    handleSubmit = (evt: React.MouseEvent<HTMLFormElement>) => {
        evt.preventDefault();
        if (this.userForm.status === 'INVALID') {
            this.userForm.markAsSubmitted();
            toastr.error(
                this.props.t('validationError'),
                '',
                constants.toastrError
            );
            return;
        }

        if (
            !this.userForm.value.tempFacility?.trim() &&
            !this.userForm.value.brandID
        ) {
            toastr.error(
                this.props.t('facilityOrCompanyRequired'),
                '',
                constants.toastrError
            );
            return;
        }

        if (this.userForm.value.email !== this.state.email) {
            this.userForm.value.email = this.state.email;
            console.log(this.userForm.value);
        }

        console.log(this.userForm.value);

        this.props.handleSubmit(this.userForm.value);
    };
    setForm = (form: AbstractControl) => {
        this.userForm = form;
        this.userForm.meta = {
            handleCancel: this.props.handleCancel,
            cancelText: 'Cancel',
            submitText: 'Sign Up',
            loading: this.props.loading
        };

        // brands can finish loading before or after the form mounts
        if (this.brandOptions.length) {
            this.setBrandOptions(this.brandOptions);
        }
    };

    render() {
        const { t } = this.props;

        return (
            <>
                <form
                    onSubmit={this.handleSubmit}
                    className="clearfix beacon-form login-form"
                >
                    <FormGenerator
                        onMount={this.setForm}
                        fieldConfig={this.fieldConfig}
                    />
                    <Col xs={12} className="user-form-buttons">
                        <Button
                            type="button"
                            onClick={this.props.handleCancel}
                            disabled={this.props.loading}
                            className="left-side btn-cancel"
                        >
                            {t('common:cancel')}
                        </Button>
                        <Button
                            bsStyle="primary"
                            type="submit"
                            disabled={this.props.loading}
                            className="pull-right btn-signup"
                        >
                            {t('signUp')}
                        </Button>
                    </Col>
                </form>
                <AddCompanyModal
                    show={this.state.showAddCompanyModal}
                    onHide={() => this.setState({ showAddCompanyModal: false })}
                    onCompanyCreated={this.handleCompanyCreated}
                />
            </>
        );
    }
}

export default withTranslation('user')(UserForm);
